/**
 * PDFViewerPanelMessageRenderer Component - Helper to render message with citations
 *
 * Extracted from PDFViewerPanel.tsx
 */

import React from 'react';
import CitationBadge from '../CitationBadge';
import type { CitationData } from './types/pdf-viewer-panel.types';

interface PDFViewerPanelMessageRendererProps {
  text: string;
  citations: Record<string, CitationData>;
  onCitationClick?: (citation: CitationData) => void;
  onChunkClick?: (citation: CitationData) => void;
}

export const PDFViewerPanelMessageRenderer: React.FC<PDFViewerPanelMessageRendererProps> = ({
  text,
  citations,
  onCitationClick,
  onChunkClick,
}) => {
  if (!text) return null;
  if (!citations || Object.keys(citations).length === 0) {
    return <span>{text}</span>;
  }

  const citationRegex = /\[(\d+)\]/g;
  const parts: (string | React.ReactNode)[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = citationRegex.exec(text)) !== null) {
    const citationNumber = match[1];
    const citationData = citations[citationNumber];

    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    if (citationData && onCitationClick) {
      parts.push(
        <CitationBadge
          key={`citation-${match.index}-${citationNumber}`}
          citationNumber={citationNumber}
          citationData={citationData}
          onOpenDocument={() => onCitationClick?.(citationData)}
          onOpenChunk={() => onChunkClick?.(citationData)}
        />
      );
    } else {
      parts.push(match[0]);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return <>{parts}</>;
};
